import { P2GAlert, P2GText, P2GGrid, P2GDivider } from '@parcel2go/core-ui';
import { PaymentOptionApiModel } from '~/lib/api/smartsend/types';
import ProviderName from '../../../../lib/helpers/ProviderName';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { SmartSendProvider } from '~/lib/consts/providers';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';

interface innerprops {
  currencyCode: string;
  paymentType: PaymentOptionApiModel;
  manifestable: boolean;
  close: () => void;
}

interface props extends innerprops {
  wrapper: (children: React.ReactNode) => React.ReactNode;
}

export const PaymentInstructions: React.FC<props> = (props) => {
  if (!props.manifestable && props.paymentType.Type === 'TopUp' && props.paymentType.Provider === SmartSendProvider) {
    return null;
  }

  return props.wrapper(
    <P2GAlert palette="primary">
      <PaymentInstructionsInner
        paymentType={props.paymentType}
        manifestable={props.manifestable}
        close={props.close}
        currencyCode={props.currencyCode}
      />
    </P2GAlert>
  );
};

export const PaymentInstructionsInner: React.FC<innerprops> = (props) => {
  const courier = ProviderName(props.paymentType.Provider);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  if (props.manifestable) {
    return (
      <P2GGrid container direction="column" spacing={1}>
        <P2GGrid item>
          <P2GText component="p" variant="body2">
            <strong>
              <Trans
                t={t}
                i18nKey="MANIFEST_PAYMENT_INSTRUCTION_ALERT"
                values={{ courier: courier }}
                defaults="After processing your orders for the day, you must generate a manifest to complete the {{courier}} end-of-day process."
              />
            </strong>
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GText component="p" variant="body2">
            <strong>
              <Trans
                t={t}
                i18nKey="MANIFEST_PAYMENT_INSTRUCTION_ALERT_SUBTEXT"
                defaults="You can easily complete this process using the prompt which will appear above your orders."
              />
            </strong>
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GDivider />
        </P2GGrid>
        <P2GGrid item>
          <P2GText component="p" variant="body2">
            <Trans
              t={t}
              i18nKey="MANIFEST_PAYMENT_INSTRUCTION_ALERT_SUBTEXT_2"
              values={{ courier: courier }}
              defaults="Any charges will be invoiced by <strong>{{courier}}</strong> directly, as per your current process, and not by <strong>Parcel2Go</strong>."
            />
          </P2GText>
        </P2GGrid>
      </P2GGrid>
    );
  }

  switch (props.paymentType.Type) {
    case 'Invoiced':
    case 'CourierAccount':
      return props.paymentType.Provider === SmartSendProvider ? (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="INVOICED_PAYMENT_INSTRUCTION_ALERT"
            values={{ courier: courier }}
            defaults="You will see these charges on your {{courier}} account."
          />
        </P2GText>
      ) : (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="INVOICED_PAYMENT_INSTRUCTION_ALERT_ALTERNATIVE"
            values={{ courier: courier, provider: SettingsCache.settings.SmartSend.Name }}
            defaults="You will not see any of these charges on your {{provider}} account, you will be invoiced directly from {{courier}}."
          />
        </P2GText>
      );
    case 'TopUp':
      return props.paymentType.Provider === SmartSendProvider ? (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="TOPUP_PAYMENT_INSTRUCTION_ALERT"
            values={{ amount: toDisplayCurrency(props.currencyCode, props.paymentType.Balance) }}
            defaults="This will be taken from your {{amount}} PrePay balance."
          />
        </P2GText>
      ) : (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="TOPUP_PAYMENT_INSTRUCTION_ALERT_ALTERNATIVE"
            values={{
              provider: SettingsCache.settings.SmartSend.Name,
              courier: ProviderName(props.paymentType.Provider)
            }}
            defaults="You will not see any of these charges on your {{provider}} account, you will be invoiced directly by {{courier}}."
          />
        </P2GText>
      );
    case 'SavedPayment':
      return (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="SAVED_PAYMENT_INSTRUCTION_ALERT"
            values={{ card: props.paymentType.Options[0]?.Display }}
            defaults="This will be taken from your card <strong>{{card}}</strong>."
          />
        </P2GText>
      );
    case 'External':
      return (
        <P2GText component="p" variant="body2">
          <Trans
            t={t}
            i18nKey="EXTERNAL_PAYMENT_INSTRUCTION_ALERT"
            defaults="This will create a payment link to complete your order. <br /> Your labels will be shown here once payment has been taken."
          />
        </P2GText>
      );
    default:
      return (
        <P2GText component="p" variant="body2">
          {t('DEFAULT_PAYMENT_INSTRUCTION_ALERT', {
            defaultValue: 'There are no payment options available to select. Please try again.'
          })}
        </P2GText>
      );
  }
};
export default PaymentInstructions;
